import {
  AtomIcon,
  FactoryIcon,
  Globe2Icon,
  GraduationCapIcon,
  MicroscopeIcon,
  RadiationIcon,
  RecycleIcon,
  ShieldCheckIcon,
  Trash2Icon,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export type Module = {
  number: string;
  title: string;
  subtitle: string;
  context: string;
  items: string[];
  result: string;
  icon: LucideIcon;
};

export const modules: Module[] = [
  {
    number: '1',
    title: 'Nuclear Energy Fundamentals: The Foundation',
    subtitle: 'The foundation your students need to understand everything else',
    context:
      'Your first introduction to nuclear energy can be transformative or intimidating. This volume transforms technical concepts into something comprehensible and relevant from day one.',
    items: [
      'What nuclear energy is and why it matters',
      'Foundations of nuclear science',
      'Matter, atoms, and nuclei',
      'Nuclear energy concepts',
      'Introduction to radioactivity',
      'Nuclear reactions',
      'Nuclear fission fundamentals',
      'Nuclear energy and electricity',
      'Historical development of nuclear energy',
      'Nuclear energy in modern society',
      'Essential terminology',
      'Foundations for further nuclear energy study',
    ],
    result:
      'Your students understand nuclear energy as a practical discipline that connects physics, engineering, and society — not an impossible list of terms to memorize.',
    icon: AtomIcon,
  },
  {
    number: '2',
    title: 'Atomic & Nuclear Science',
    subtitle: 'Everything your students need to know about atomic and nuclear structure',
    context:
      'This is where many students struggle to build the scientific foundation. This volume gives you the materials to help them truly understand nuclear science.',
    items: [
      'Atomic structure',
      'Protons, neutrons, and electrons',
      'Atomic number and mass number',
      'Isotopes',
      'Atomic nuclei',
      'Nuclear stability',
      'Nuclear forces',
      'Mass and energy',
      'Nuclear transformations',
      'Nuclear reactions',
      'Fundamental nuclear terminology',
      'Connecting atomic science with nuclear energy',
      '12 practical atomic science case studies',
    ],
    result: 'Your students learn the scientific principles that underpin all nuclear energy processes.',
    icon: MicroscopeIcon,
  },
  {
    number: '3',
    title: 'Radioactivity, Radiation & Nuclear Fission',
    subtitle: 'How to understand radiation and fission from a clear educational perspective',
    context:
      'Radioactivity and fission are complex but essential to understand. This volume gives you everything needed so your students grasp these processes clearly.',
    items: [
      'Radioactive decay',
      'Types of radiation',
      'Alpha, beta, and gamma radiation',
      'Half-life',
      'Radiation and matter',
      'Natural and human-made radiation',
      'Radiation in everyday contexts',
      'Nuclear fission',
      'Chain reactions at a conceptual level',
      'Energy released through fission',
      'Common misconceptions about radiation',
      'Classroom explanations of radiation and fission',
      '15 worked radioactivity and fission examples',
    ],
    result:
      'Your students understand radiation and fission as natural phenomena that can be understood, measured, and managed responsibly.',
    icon: RadiationIcon,
  },
  {
    number: '4',
    title: 'Nuclear Reactors & Power Plants',
    subtitle: 'How nuclear reactors and power plants generate electricity',
    context:
      'Nuclear reactors are complex systems but comprehensible. This volume makes the process clear without requiring operational procedures.',
    items: [
      'Nuclear reactor fundamentals',
      'Reactor components and their broad functions',
      'Reactor cores',
      'Nuclear fuel',
      'Moderators and coolants',
      'Heat production and transfer',
      'Steam generation',
      'Electricity generation',
      'Pressurized Water Reactors',
      'Boiling Water Reactors',
      'Heavy-water reactors',
      'Advanced reactors and Small Modular Reactors',
      'Safety systems and barriers',
      'Nuclear power plants within electricity systems',
      '12 practical nuclear reactor case studies',
    ],
    result: 'Your students understand how nuclear reactors work and the multiple safety systems that protect them.',
    icon: FactoryIcon,
  },
  {
    number: '5',
    title: 'The Nuclear Fuel Cycle',
    subtitle: 'A complete overview of nuclear fuel from resource to disposal',
    context:
      'The fuel cycle is interconnected but often fragmented in teaching. This volume connects all the pieces.',
    items: [
      'Understanding the nuclear fuel cycle',
      'Uranium as a natural resource',
      'From uranium resource to nuclear fuel',
      'Nuclear fuel inside reactors',
      'Used nuclear fuel',
      'Used fuel storage',
      'Reprocessing and nuclear fuel recycling',
      'Radioactive waste from the fuel cycle',
      'Long-term management',
      'Geological disposal',
      'Environmental considerations',
      'Future nuclear fuel concepts',
      'Teaching the nuclear fuel cycle',
      '15 practical fuel cycle case studies',
    ],
    result: 'Your students understand the complete journey of nuclear fuel and why responsible stewardship matters.',
    icon: RecycleIcon,
  },
  {
    number: '6',
    title: 'Nuclear Safety, Radiation Protection & Risk',
    subtitle: 'Teaching nuclear safety and risk using evidence-focused approaches',
    context: 'Nuclear safety is critical and often misunderstood. This volume helps you teach it responsibly.',
    items: [
      'Understanding nuclear safety',
      'Safety culture',
      'Defense-in-depth',
      'Radiation protection fundamentals',
      'Radiation dose and exposure',
      'Understanding radiation risk',
      'Radiation and environmental monitoring',
      'Emergency preparedness concepts',
      'Learning from nuclear events',
      'Three Mile Island',
      'Chernobyl',
      'Fukushima',
      'Nuclear safety and the environment',
      'Risk communication',
      'Public perception',
      'Teaching nuclear safety responsibly',
      '15 practical safety and risk case studies',
    ],
    result:
      'Your students understand nuclear safety as a scientific discipline built on evidence, learning, and continuous improvement.',
    icon: ShieldCheckIcon,
  },
  {
    number: '7',
    title: 'Nuclear Waste & Environmental Management',
    subtitle: 'Radioactive waste, disposal, and long-term stewardship',
    context: 'Nuclear waste is important to understand clearly and factually. This volume does exactly that.',
    items: [
      'Understanding radioactive waste',
      'Types and classification of waste',
      'Sources of nuclear waste',
      'Waste management principles',
      'Treatment, conditioning, and containment',
      'Radioactive waste storage',
      'Disposal concepts',
      'Deep geological repositories',
      'Environmental monitoring',
      'Environmental protection',
      'Nuclear facility decommissioning',
      'Sustainability and long-term stewardship',
      'Nuclear waste and society',
      'Classroom discussions and critical thinking',
      '15 practical waste management case studies',
    ],
    result:
      'Your students understand that radioactive waste is managed through rigorous science and responsible stewardship.',
    icon: Trash2Icon,
  },
  {
    number: '8',
    title: 'Nuclear Energy in Society, Economics, Climate & The Global Energy System',
    subtitle: "Nuclear energy's role in addressing global challenges",
    context:
      'This is the practical heart. Your students learn how nuclear energy contributes to energy security and climate solutions.',
    items: [
      'Nuclear energy in modern society',
      'The global energy system',
      'Nuclear electricity',
      'Nuclear energy economics',
      'Nuclear energy and climate change',
      'Energy security',
      'Reliability',
      'Nuclear and renewable energy',
      'Nuclear energy around the world',
      'Public perception and debate',
      'Policy and regulation',
      'Energy decision-making',
      'Future nuclear technologies',
      'The future role of nuclear energy',
      'Evidence-based classroom discussion',
      '15 real-world nuclear energy case studies',
    ],
    result:
      'Your students understand that nuclear energy is part of diverse energy solutions and contributes meaningfully to addressing climate challenges.',
    icon: Globe2Icon,
  },
  {
    number: '9',
    title: 'Teaching Nuclear Energy: Classroom Excellence',
    subtitle: 'Your complete guide as a nuclear energy education professional',
    context:
      'This volume gives you exactly what you need to plan, organize, and evaluate your entire year of nuclear energy teaching.',
    items: [
      'Building foundations for teaching nuclear energy',
      'Sequencing nuclear energy concepts',
      'Explaining difficult concepts',
      'Addressing misconceptions about nuclear energy',
      'Classroom activities',
      'Questions and discussions',
      'Teaching radiation and safety responsibly',
      'Nuclear energy, climate, and environment',
      'Case studies',
      'Classroom debates and critical thinking',
      'Assessing student understanding',
      'Planning lessons and learning sequences',
      'Building a complete nuclear energy learning experience',
      '10 complete integrated nuclear energy projects',
    ],
    result:
      'You\'ll never wonder "what do I teach today?" again. Everything is planned and contextualized for maximum student learning.',
    icon: GraduationCapIcon,
  },
];
